import { useCallback, useId, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, Input, Switch, TextArea } from '@aimsos/ui';

/**
 * New folder (CT-5717): Name (required), what belongs in it (optional — agents
 * file to it), and whether Concierge extracts claims from it (on by default).
 * Create folder stays disabled until a name is typed.
 */
function NewFolderForm({ creating, onSubmit, onCancel }) {
  const { t } = useTranslation('unified-profiles');
  const descriptionId = useId();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [extractClaims, setExtractClaims] = useState(true);

  const handleName = useCallback((event) => setName(event.target.value), []);
  const handleDescription = useCallback((event) => setDescription(event.target.value), []);

  const canCreate = name.trim() !== '' && !creating;
  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!canCreate) return;
      onSubmit({ name, description, extractClaims });
    },
    [canCreate, onSubmit, name, description, extractClaims],
  );

  return (
    <form onSubmit={handleSubmit} noValidate data-testid="new-folder-form" className="flex flex-col gap-5">
      <span className="text-xs font-semibold uppercase tracking-wide text-content-muted">{t('knowledge.drive.newFolder.section')}</span>
      <Input
        label={t('knowledge.drive.newFolder.name')}
        value={name}
        onChange={handleName}
        placeholder={t('knowledge.drive.newFolder.namePlaceholder')}
        disabled={creating}
        required
      />
      <div className="flex flex-col gap-1.5">
        <label htmlFor={descriptionId} className="text-sm font-medium text-content-secondary">
          {t('knowledge.drive.newFolder.description')}
        </label>
        <TextArea
          id={descriptionId}
          rows={3}
          value={description}
          onChange={handleDescription}
          placeholder={t('knowledge.drive.newFolder.descriptionPlaceholder')}
          disabled={creating}
        />
      </div>
      <Switch
        checked={extractClaims}
        onChange={setExtractClaims}
        disabled={creating}
        label={t('knowledge.drive.newFolder.extract')}
        description={t('knowledge.drive.newFolder.extractHint')}
      />
      <div className="flex justify-end gap-3 border-t border-edge pt-4">
        <Button variant="secondary" size="md" onClick={onCancel} disabled={creating}>
          {t('knowledge.drive.newFolder.cancel')}
        </Button>
        <Button type="submit" variant="primary" size="md" disabled={!canCreate}>
          {t('knowledge.drive.newFolder.create')}
        </Button>
      </div>
    </form>
  );
}

export default NewFolderForm;
